import type {
  CreateOrgAdminKeyRequest,
  CreateOrgAdminKeyResponse,
  ListOrgAdminKeysResponse,
  OrgAdminKeySummary,
} from '@nadoutong/contracts';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { createOrgAdminKey, listOrgAdminKeys, revokeOrgAdminKey } from './adminKeys.ts';

/**
 * React Query wrappers for `/v1/org/admin-keys`.
 *
 * The list is the only read the page needs: create and revoke both
 * invalidate `['org-admin-keys']`. Pass `enabled: false` when the
 * caller is not an owner or admin so the 403 is never issued.
 */

export const ORG_ADMIN_KEYS_QUERY_KEY = ['org-admin-keys'] as const;

export function useOrgAdminKeysList(enabled = true): UseQueryResult<ListOrgAdminKeysResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ORG_ADMIN_KEYS_QUERY_KEY,
    queryFn: ({ signal }) => listOrgAdminKeys(fetchImpl, signal),
    enabled,
  });
}

export function useCreateOrgAdminKey(): UseMutationResult<
  CreateOrgAdminKeyResponse,
  Error,
  CreateOrgAdminKeyRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createOrgAdminKey(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ORG_ADMIN_KEYS_QUERY_KEY });
    },
  });
}

export function useRevokeOrgAdminKey(): UseMutationResult<OrgAdminKeySummary, Error, string> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => revokeOrgAdminKey(id, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ORG_ADMIN_KEYS_QUERY_KEY });
    },
  });
}
